import { forwardRef, useState, useEffect } from 'react';
import type { InputHTMLAttributes } from 'react';
import clsx from 'clsx';
import { formatDuration, extractRawDigits } from './DurationInput.utils';

export interface DurationInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> {
  label?: string;
  error?: string;
  helperText?: string;
  value?: string;
  onChange?: (value: string) => void;
}

function extractDigits(value: string): string {
  return value.replace(/\D/g, '');
}

// Brand colors - exact match with elka-manager
const BRAND_BLUE = '#3C5FA8';
const ERROR_RED = '#f43f5e';

export const DurationInput = forwardRef<HTMLInputElement, DurationInputProps>(
  (
    { label, error, helperText, className, value, onChange, onBlur, onFocus, style, ...props },
    ref
  ) => {
    const [rawDigits, setRawDigits] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isUserTyping, setIsUserTyping] = useState(false);

    // Sync with external value only when not actively typing
    useEffect(() => {
      if (value !== undefined && !isUserTyping) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- sync external controlled value to internal display state
        setRawDigits(extractRawDigits(value));
      }
    }, [value, isUserTyping]);

    // Reset typing flag after a short delay
    useEffect(() => {
      if (isUserTyping) {
        const timer = setTimeout(() => setIsUserTyping(false), 100);
        return () => clearTimeout(timer);
      }
    }, [isUserTyping, rawDigits]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const digits = extractDigits(inputValue);

      // Limit to 4 digits
      const limitedDigits = digits.slice(0, 4);
      setIsUserTyping(true);
      setRawDigits(limitedDigits);

      // Notify parent with formatted value
      if (onChange) {
        const formatted = formatDuration(limitedDigits);
        onChange(formatted);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    };

    // Focus ring via inline style
    const focusRingColor = error ? ERROR_RED : BRAND_BLUE;
    const inputStyle: React.CSSProperties = {
      ...(isFocused
        ? {
            boxShadow: `0 0 0 2px ${focusRingColor}`,
            borderColor: 'transparent',
            outline: 'none',
          }
        : {}),
      ...style,
    };

    // Display formatted value
    const displayValue = rawDigits ? formatDuration(rawDigits) : '';

    return (
      <div className="w-full">
        {label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {label}
            {props.required && <span className="mr-1 text-rose-500">*</span>}
          </label>
        )}
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className={clsx(
            'min-h-[44px] w-full rounded-lg border px-3 py-2.5 shadow-sm md:min-h-0 md:py-2',
            'focus:border-transparent focus:ring-2 focus:ring-[#3C5FA8] focus:outline-none',
            'disabled:cursor-not-allowed disabled:bg-gray-100',
            'text-base tabular-nums', // Prevents zoom on iOS
            error ? 'border-rose-300 focus:ring-rose-500' : 'border-gray-300',
            className
          )}
          style={inputStyle}
          {...props}
        />
        {error && <p className="mt-1 text-sm text-rose-600">{error}</p>}
        {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
      </div>
    );
  }
);

DurationInput.displayName = 'DurationInput';
